"use client";

import type { AnchorHTMLAttributes, ReactNode } from "react";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import type { Localized } from "./types";

/** Picks the current locale's string; Khmer is the site default. */
export function useText() {
  const locale = useLocale();
  const isKhmer = locale !== "en";
  const text = (value: Localized) =>
    isKhmer ? value.km || value.en : value.en || value.km;
  return { text, isKhmer, locale };
}

/** Interface copy that isn't event content. */
export const UI = {
  pauseSlides: { km: "ផ្អាកស្លាយ", en: "Pause slideshow" },
  resumeSlides: { km: "បន្តស្លាយ", en: "Resume slideshow" },
  exploreEvent: { km: "ស្វែងយល់ពីព្រឹត្តិការណ៍", en: "Explore Event" },
  viewProgram: { km: "មើលកម្មវិធី", en: "View Program" },
  eventDays: { km: "ថ្ងៃព្រឹត្តិការណ៍", en: "Event days" },
  day: { km: "ថ្ងៃទី", en: "Day" },
  schedule: { km: "កម្មវិធី", en: "Schedule" },
  photoHighlights: { km: "រូបភាពសកម្មភាព", en: "Photo Highlights" },
  videoHighlights: { km: "វីដេអូសកម្មភាព", en: "Video Highlights" },
  viewMore: { km: "មើលបន្ថែម", en: "View more" },
  competition: { km: "ការប្រកួតប្រជែង", en: "Competition" },
  finalRound: { km: "វគ្គផ្តាច់ព្រ័ត្រ", en: "Final Round" },
  performances: { km: "ការសម្តែង", en: "Performances" },
  lineup: { km: "កម្មវិធីសម្តែង", en: "Performance line-up" },
  minutes: { km: "នាទី", en: "min" },
  statistics: { km: "ទំហំព្រឹត្តិការណ៍", en: "Event at a glance" },
  highlights: { km: "ចំណុចពិសេសនៃព្រឹត្តិការណ៍", en: "Event Highlights" },
  highlightsIntro: {
    km: "ពិធីបើក ការប្រកួតធ្វើម្ហូប KOLs កម្មវិធីអាជីវកម្ម ការសម្តែង និងការប្រគុំតន្ត្រី",
    en: "Opening, cooking, KOLs, business, performances and the giant concert.",
  },
  sponsors: { km: "អ្នកឧបត្ថម្ភ និងដៃគូ", en: "Sponsors & Partners" },
  ctaTitle: {
    km: "ចូលរួមបទពិសោធន៍ ពិព័រណ៍អាហារកម្ពុជា",
    en: "Experience Cambodia Food Expo",
  },
  contactUs: { km: "ទំនាក់ទំនងយើង", en: "Contact Us" },
  learnMore: { km: "ស្វែងយល់បន្ថែម", en: "Learn More" },
  play: { km: "ចាក់វីដេអូ", en: "Play video" },
  close: { km: "បិទ", en: "Close" },
  previous: { km: "មុន", en: "Previous" },
  next: { km: "បន្ទាប់", en: "Next" },
  zoomIn: { km: "ពង្រីក", en: "Zoom in" },
  zoomOut: { km: "បង្រួម", en: "Zoom out" },
  slide: { km: "ស្លាយ", en: "Slide" },
} satisfies Record<string, Localized>;

/**
 * In-page anchor that smooth-scrolls without leaving a hash in the URL.
 * Sections carry scroll-margin so they land below the sticky header + day nav.
 */
export function SectionLink({
  to,
  children,
  ...rest
}: { to: string; children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "onClick"
>) {
  return (
    <a
      {...rest}
      href={`#${to}`}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        const element = document.getElementById(to);
        if (!element) return;
        event.preventDefault();
        const reduce = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        element.scrollIntoView({
          behavior: reduce ? "auto" : "smooth",
          block: "start",
        });
        element.setAttribute("tabindex", "-1");
        element.focus({ preventScroll: true });
      }}
    >
      {children}
    </a>
  );
}

/** Small uppercase label above headings. Latin-only tracking keeps Khmer glyphs intact. */
export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { isKhmer } = useText();
  return (
    <p
      className={cn(
        "text-sm font-semibold text-[var(--event-accent)]",
        !isKhmer && "uppercase tracking-[0.22em]",
        className,
      )}
    >
      {children}
    </p>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
  id,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  align?: "center" | "left";
  id?: string;
}) {
  return (
    <div
      className={cn(
        "mb-10 md:mb-12",
        align === "center" && "mx-auto max-w-3xl text-center",
      )}
    >
      {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
      <h2
        id={id}
        className="text-[1.75rem] font-bold leading-[1.45] text-[var(--event-ink)] md:text-[2.5rem]"
      >
        {title}
      </h2>
      {description && (
        <p className="mt-4 text-base leading-8 text-[var(--event-muted)] md:text-lg">
          {description}
        </p>
      )}
    </div>
  );
}

/** h4-level title for gallery / schedule blocks inside a period. */
export function BlockTitle({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <h4 className="mb-5 flex items-center gap-2.5 text-lg font-semibold leading-relaxed text-[var(--event-ink)] md:text-xl">
      {icon && <span className="text-[var(--event-accent)]">{icon}</span>}
      {children}
    </h4>
  );
}
